// API service for Adina's Crafts
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

// Client-side cache for products
let productsCache = {
  data: null,
  timestamp: null,
  ttl: 30000  // 30 seconds cache
};

// Generic fetch wrapper with error handling and timeout
async function fetchAPI(endpoint, options = {}) {
  const url = `${API_BASE_URL}/api${endpoint}`;
  
  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };

  // Add timeout using AbortController
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout

  try {
    const response = await fetch(url, { 
      ...defaultOptions, 
      ...options,
      signal: controller.signal 
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'An error occurred' }));
      throw new Error(error.detail || `HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timed out. Please check your connection and try again.');
    }
    throw error;
  }
}

// Products API
export const productsAPI = {
  // Get all products with client-side caching
  getAll: async (filters = {}) => {
    // Check if we can use cache (only for unfiltered requests)
    const hasFilters = filters.minPrice || filters.maxPrice || filters.category || 
                       filters.badges || filters.inStock !== undefined || filters.sort;
    
    if (!hasFilters && productsCache.data) {
      const cacheAge = Date.now() - (productsCache.timestamp || 0);
      if (cacheAge < productsCache.ttl) {
        console.log('Using cached products');
        return productsCache.data;
      }
    }
    
    const params = new URLSearchParams();
    if (filters.minPrice) params.append('minPrice', filters.minPrice);
    if (filters.maxPrice) params.append('maxPrice', filters.maxPrice);
    if (filters.category) params.append('category', filters.category);
    if (filters.badges) params.append('badges', filters.badges);
    if (filters.inStock !== undefined) params.append('inStock', filters.inStock);
    if (filters.sort) params.append('sort', filters.sort);
    
    const queryString = params.toString();
    const endpoint = queryString ? `/products?${queryString}` : '/products';
    const data = await fetchAPI(endpoint);
    
    // Cache unfiltered results
    if (!hasFilters) {
      productsCache.data = data;
      productsCache.timestamp = Date.now();
    }
    
    return data;
  },

  // Invalidate cache (call after product changes)
  invalidateCache: () => {
    productsCache.data = null;
    productsCache.timestamp = null;
  },

  // Get single product by ID
  getById: async (id) => {
    return fetchAPI(`/products/${id}`);
  },

  // Create new product
  create: async (product) => {
    return fetchAPI('/products', {
      method: 'POST',
      body: JSON.stringify(product),
    });
  },

  // Update product
  update: async (id, product) => {
    return fetchAPI(`/products/${id}`, {
      method: 'PUT',
      body: JSON.stringify(product),
    });
  },

  // Delete product
  delete: async (id) => {
    return fetchAPI(`/products/${id}`, {
      method: 'DELETE',
    });
  },

  // Upload image (returns base64 data URL)
  uploadImage: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const url = `${API_BASE_URL}/api/products/upload-image`;
    const response = await fetch(url, {
      method: 'POST',
      body: formData,
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
      throw new Error(error.detail || 'Failed to upload image');
    }
    
    return response.json();
  },

  // Seed initial products
  seed: async () => {
    return fetchAPI('/products/seed', { method: 'POST' });
  },
};

// Orders API
export const ordersAPI = {
  // Get all orders (admin)
  getAll: async (status = null) => {
    const endpoint = status ? `/orders?status=${status}` : '/orders';
    return fetchAPI(endpoint);
  },

  // Get single order
  getById: async (id) => {
    return fetchAPI(`/orders/${id}`);
  },

  // Create checkout session
  createCheckout: async (order) => {
    const originUrl = window.location.origin;
    return fetchAPI('/orders/checkout', {
      method: 'POST',
      body: JSON.stringify({ order, originUrl }),
    });
  },

  // Get checkout status
  getCheckoutStatus: async (sessionId) => {
    return fetchAPI(`/orders/checkout/status/${sessionId}`);
  },

  // Update order status (admin)
  updateStatus: async (orderId, status) => {
    return fetchAPI(`/orders/${orderId}/status?status=${status}`, {
      method: 'PUT',
    });
  },

  // Add tracking number (admin)
  addTracking: async (orderId, trackingNumber) => {
    return fetchAPI(`/orders/${orderId}/tracking`, {
      method: 'PUT',
      body: JSON.stringify({ trackingNumber }),
    });
  },

  // Delete order (admin)
  delete: async (orderId) => {
    return fetchAPI(`/orders/${orderId}`, {
      method: 'DELETE',
    });
  },
};

// Contact API
export const contactAPI = {
  // Send contact message
  send: async (message) => {
    return fetchAPI('/contact', {
      method: 'POST',
      body: JSON.stringify(message),
    });
  },
};

// Cloudinary API for image uploads
export const cloudinaryAPI = {
  // Get signature for direct upload to Cloudinary
  getSignature: async (resourceType = 'image', folder = 'products') => {
    return fetchAPI(`/cloudinary/signature?resource_type=${resourceType}&folder=${folder}`);
  },

  // Upload image directly to Cloudinary using signed upload
  uploadImage: async (file) => {
    try {
      // Get signature from backend
      const sig = await cloudinaryAPI.getSignature('image', 'products');
      
      // Create form data for Cloudinary upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('api_key', sig.api_key);
      formData.append('timestamp', sig.timestamp);
      formData.append('signature', sig.signature);
      formData.append('folder', sig.folder);
      
      // Upload directly to Cloudinary
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
        { method: 'POST', body: formData }
      );
      
      if (!response.ok) {
        throw new Error('Failed to upload image to Cloudinary');
      }
      
      const result = await response.json();
      return {
        url: result.secure_url,
        publicId: result.public_id,
        width: result.width,
        height: result.height
      };
    } catch (error) {
      console.error('Cloudinary upload error:', error);
      throw error;
    }
  },

  // Upload base64 image via backend
  uploadBase64: async (base64Data, folder = 'products') => {
    return fetchAPI('/cloudinary/upload', {
      method: 'POST',
      body: JSON.stringify({ image: base64Data, folder }),
    });
  },

  // Get optimized URL for an image
  getOptimizedUrl: (url, width = 400) => {
    if (!url || !url.includes('cloudinary')) {
      return url;
    }
    // Add transformations to Cloudinary URL
    return url.replace('/upload/', `/upload/w_${width},q_auto,f_auto/`);
  },

  // Get thumbnail URL
  getThumbnailUrl: (url) => {
    return cloudinaryAPI.getOptimizedUrl(url, 300);
  }
};

export default { productsAPI, ordersAPI, contactAPI, cloudinaryAPI };

